import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Image as ImageIcon,
  Package,
  Search,
  Grid,
  List,
  Tag,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { CategoryService } from '../../services/categoryService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';

const CategoryManagement = () => {
  const { addNotification } = useNotification();
  const location = useLocation();

  const requestedSection = useMemo(() => {
    if (location.pathname === '/admin/topup/categories') {
      return CategoryService.CATEGORY_SECTIONS.TOPUP;
    }

    const params = new URLSearchParams(location.search);
    return params.get('section') === CategoryService.CATEGORY_SECTIONS.TOPUP
      ? CategoryService.CATEGORY_SECTIONS.TOPUP
      : CategoryService.CATEGORY_SECTIONS.CARDS;
  }, [location.pathname, location.search]);
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState(requestedSection);
  const [viewMode, setViewMode] = useState('grid');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    status: 'active',
    section: CategoryService.CATEGORY_SECTIONS.CARDS,
    type: CategoryService.CATEGORY_TYPES.CATEGORY,
    parentCategoryId: '',
    parentCategoryName: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const topupParentCategories = useMemo(() => {
    return categories.filter((category) => CategoryService.isTopupParentCategory(category));
  }, [categories]);

  useEffect(() => {
    loadCategories();
  }, [sectionFilter]);

  useEffect(() => {
    setSectionFilter(requestedSection);
  }, [requestedSection]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const categoriesData = await CategoryService.getCategoriesWithProductCount(sectionFilter);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      addNotification('error', 'Erreur de chargement', 'Impossible de charger les catégories');
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = useMemo(() => categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.parentCategoryName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || category.status === statusFilter;
    return matchesSearch && matchesStatus;
  }), [categories, searchTerm, statusFilter]);

  const groupedCategorySections = useMemo(() => {
    const parentCategories = filteredCategories.filter((category) => (
      !CategoryService.isTopupSubcategory(category)
    ));
    const subcategories = filteredCategories.filter((category) => (
      CategoryService.isTopupSubcategory(category)
    ));

    if (sectionFilter === CategoryService.CATEGORY_SECTIONS.TOPUP) {
      return [
        {
          key: 'topup-categories',
          title: 'Categories Topup',
          description: 'Affichage reserve aux categories parentes.',
          emptyLabel: 'Aucune categorie Topup',
          items: parentCategories
        },
        {
          key: 'topup-subcategories',
          title: 'Sous-categories Topup',
          description: 'Affichage reserve aux sous-categories liees.',
          emptyLabel: 'Aucune sous-categorie Topup',
          items: subcategories
        }
      ];
    }

    return [
      {
        key: 'card-categories',
        title: 'Categories Cartes',
        description: 'Affichage dedie aux categories de cartes.',
        emptyLabel: 'Aucune categorie carte',
        items: parentCategories
      }
    ];
  }, [filteredCategories, sectionFilter]);

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        imageUrl: category.imageUrl || '',
        status: category.status,
        section: category.section || CategoryService.CATEGORY_SECTIONS.CARDS,
        type: category.type || CategoryService.CATEGORY_TYPES.CATEGORY,
        parentCategoryId: category.parentCategoryId || '',
        parentCategoryName: category.parentCategoryName || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        imageUrl: '',
        status: 'active',
        section: sectionFilter,
        type: CategoryService.CATEGORY_TYPES.CATEGORY,
        parentCategoryId: '',
        parentCategoryName: ''
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      imageUrl: '',
      status: 'active',
      section: sectionFilter,
      type: CategoryService.CATEGORY_TYPES.CATEGORY,
      parentCategoryId: '',
      parentCategoryName: ''
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Le nom de la catégorie est requis';
    }

    if (
      formData.section === CategoryService.CATEGORY_SECTIONS.TOPUP &&
      formData.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY &&
      !String(formData.parentCategoryId || '').trim()
    ) {
      errors.parentCategoryId = 'La categorie parente est requise pour une sous-categorie Topup';
    }
    
    if (formData.imageUrl && !isValidUrl(formData.imageUrl)) {
      errors.imageUrl = 'URL d\'image invalide';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    try {
      const selectedParentCategory = topupParentCategories.find(
        (category) => category.id === formData.parentCategoryId
      ) || null;
      const payload = {
        ...formData,
        parentCategoryName: formData.section === CategoryService.CATEGORY_SECTIONS.TOPUP &&
          formData.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY
          ? (selectedParentCategory?.name || '')
          : ''
      };

      if (editingCategory) {
        await CategoryService.updateCategory(editingCategory.id, payload);
        addNotification(
          'success',
          payload.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY ? 'Sous-categorie mise a jour' : 'Categorie mise a jour',
          payload.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY
            ? 'La sous-categorie a ete mise a jour avec succes'
            : 'La categorie a ete mise a jour avec succes'
        );
      } else {
        // Vérifier si le nom existe déjà
        const exists = await CategoryService.categoryExistsByName(
          payload.name,
          null,
          payload.section,
          {
            type: payload.type,
            parentCategoryId: payload.parentCategoryId
          }
        );
        if (exists) {
          setFormErrors({ name: payload.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY ? 'Une sous-categorie avec ce nom existe deja dans cette categorie' : 'Une categorie avec ce nom existe deja' });
          return;
        }
        
        await CategoryService.createCategory(payload);
        addNotification(
          'success',
          payload.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY ? 'Sous-categorie creee' : 'Categorie creee',
          payload.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY
            ? 'La sous-categorie a ete creee avec succes'
            : 'La categorie a ete creee avec succes'
        );
      }
      
      handleCloseModal();
      loadCategories();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      addNotification('error', 'Erreur de sauvegarde', 'Impossible de sauvegarder la catégorie');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (categoryId) => {
    try {
      await CategoryService.toggleCategoryStatus(categoryId);
      addNotification('success', 'Statut modifié', 'Le statut de la catégorie a été modifié');
      loadCategories();
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      addNotification('error', 'Erreur', 'Impossible de modifier le statut');
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    const category = categories.find((entry) => entry.id === categoryId);
    if (category && CategoryService.isTopupParentCategory(category) && Number(category.subcategoryCount || 0) > 0) {
      addNotification('error', 'Suppression bloquee', 'Supprimez ou reaffectez d abord les sous-categories liees a cette categorie Topup.');
      return;
    }

    if (window.confirm(`Êtes-vous sûr de vouloir supprimer la catégorie "${categoryName}" ?`)) {
      try {
        await CategoryService.deleteCategory(categoryId);
        addNotification('success', 'Catégorie supprimée', 'La catégorie a été supprimée avec succès');
        loadCategories();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        addNotification('error', 'Erreur de suppression', 'Impossible de supprimer la catégorie');
      }
    }
  };

  const renderCategoryCard = (category) => (
    <div
      key={category.id}
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${
        viewMode === 'list' ? 'flex items-center' : ''
      }`}
    >
      <div className={`${viewMode === 'list' ? 'w-24 h-24 flex-shrink-0' : 'h-48'} bg-gray-100 flex items-center justify-center`}>
        {category.imageUrl ? (
          <img
            src={category.imageUrl}
            alt={category.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`${category.imageUrl ? 'hidden' : 'flex'} items-center justify-center w-full h-full`}>
          <ImageIcon className="w-8 h-8 text-gray-400" />
        </div>
      </div>

      <div className={`p-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
        <div className={`${viewMode === 'list' ? 'flex items-center justify-between' : ''}`}>
          <div className={viewMode === 'list' ? 'flex-1' : ''}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-blue-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-blue-600">
                  {category.section === CategoryService.CATEGORY_SECTIONS.TOPUP ? 'Topup' : 'Cartes'}
                </span>
                {category.section === CategoryService.CATEGORY_SECTIONS.TOPUP ? (
                  <span className={`rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                    category.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-violet-50 text-violet-600'
                  }`}>
                    {category.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY ? 'Sous-categorie' : 'Categorie'}
                  </span>
                ) : null}
                {category.status === 'active' ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
              </div>
            </div>
            
            {category.description && (
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                {category.description}
              </p>
            )}

            {category.parentCategoryName ? (
              <div className="mb-3 inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                Parent: {category.parentCategoryName}
              </div>
            ) : null}
            
            <div className="flex items-center text-sm text-gray-500 mb-4">
              <Package className="w-4 h-4 mr-1" />
              <span>{category.productCount || 0} produit{(category.productCount || 0) > 1 ? 's' : ''}</span>
            </div>
            {category.section === CategoryService.CATEGORY_SECTIONS.TOPUP &&
            category.type !== CategoryService.CATEGORY_TYPES.SUBCATEGORY &&
            Number(category.subcategoryCount || 0) > 0 ? (
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <Tag className="w-4 h-4 mr-1" />
                <span>{category.subcategoryCount} sous-categorie{category.subcategoryCount > 1 ? 's' : ''}</span>
              </div>
            ) : null}
          </div>

          <div className={`flex items-center gap-2 ${viewMode === 'list' ? 'ml-4' : ''}`}>
            <button
              onClick={() => handleOpenModal(category)}
              className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
              title="Modifier"
            >
              <Edit className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => handleToggleStatus(category.id)}
              className={`p-2 transition-colors ${
                category.status === 'active' 
                  ? 'text-gray-400 hover:text-orange-600' 
                  : 'text-gray-400 hover:text-green-600'
              }`}
              title={category.status === 'active' ? 'Désactiver' : 'Activer'}
            >
              {category.status === 'active' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            
            <button
              onClick={() => handleDelete(category.id, category.name)}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
              title="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {sectionFilter === CategoryService.CATEGORY_SECTIONS.TOPUP
                ? 'Gestion des categories Topup'
                : 'Gestion des catégories'}
            </h1>
            <p className="text-gray-600 mt-1">
              {categories.length} entree{categories.length > 1 ? 's' : ''} au total
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle catégorie
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher une catégorie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value={CategoryService.CATEGORY_SECTIONS.CARDS}>Catégories cartes</option>
              <option value={CategoryService.CATEGORY_SECTIONS.TOPUP}>Catégories topup</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actives</option>
              <option value="inactive">Inactives</option>
            </select>
            
            <div className="flex border border-gray-300 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Display */}
      {filteredCategories.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune catégorie trouvée</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || statusFilter !== 'all' 
              ? 'Aucune entree ne correspond a vos criteres de recherche.'
              : 'Commencez par creer votre premiere categorie ou sous-categorie.'
            }
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <button
              onClick={() => handleOpenModal()}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer une catégorie
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {groupedCategorySections.map((group) => (
            <section key={group.key} className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{group.title}</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {group.items.length} entree{group.items.length > 1 ? 's' : ''} visible{group.items.length > 1 ? 's' : ''}. {group.description}
                  </p>
                </div>
              </div>

              {group.items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
                  <Tag className="mx-auto mb-3 h-8 w-8 text-gray-300" />
                  <p className="text-sm font-medium text-gray-700">{group.emptyLabel}</p>
                  <p className="mt-1 text-sm text-gray-500">
                    Ajustez vos filtres ou creez une nouvelle entree pour cette section.
                  </p>
                </div>
              ) : (
                <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
                  {group.items.map((category) => renderCategoryCard(category))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingCategory
                  ? (formData.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY ? 'Modifier la sous-categorie' : 'Modifier la categorie')
                  : (formData.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY ? 'Nouvelle sous-categorie' : 'Nouvelle categorie')}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY ? 'Nom de la sous-categorie *' : 'Nom de la categorie *'}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={formData.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY ? 'Ex: Tiktok' : 'Ex: Reseaux sociaux'}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Section
                  </label>
                  <select
                    value={formData.section}
                    onChange={(e) => setFormData((current) => ({
                      ...current,
                      section: e.target.value,
                      type: e.target.value === CategoryService.CATEGORY_SECTIONS.TOPUP
                        ? current.type
                        : CategoryService.CATEGORY_TYPES.CATEGORY,
                      parentCategoryId: e.target.value === CategoryService.CATEGORY_SECTIONS.TOPUP
                        ? current.parentCategoryId
                        : '',
                      parentCategoryName: ''
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value={CategoryService.CATEGORY_SECTIONS.CARDS}>Cartes</option>
                    <option value={CategoryService.CATEGORY_SECTIONS.TOPUP}>Topup</option>
                  </select>
                </div>

                {formData.section === CategoryService.CATEGORY_SECTIONS.TOPUP ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type d entree
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData((current) => ({
                          ...current,
                          type: e.target.value,
                          parentCategoryId: e.target.value === CategoryService.CATEGORY_TYPES.SUBCATEGORY
                            ? current.parentCategoryId
                            : '',
                          parentCategoryName: ''
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value={CategoryService.CATEGORY_TYPES.CATEGORY}>Categorie Topup</option>
                        <option value={CategoryService.CATEGORY_TYPES.SUBCATEGORY}>Sous-categorie Topup</option>
                      </select>
                    </div>

                    {formData.type === CategoryService.CATEGORY_TYPES.SUBCATEGORY ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Categorie parente *
                        </label>
                        <select
                          value={formData.parentCategoryId}
                          onChange={(e) => {
                            const parentCategory = topupParentCategories.find((category) => category.id === e.target.value) || null;
                            setFormData((current) => ({
                              ...current,
                              parentCategoryId: e.target.value,
                              parentCategoryName: parentCategory?.name || ''
                            }));
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                            formErrors.parentCategoryId ? 'border-red-300' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Selectionner une categorie parente</option>
                          {topupParentCategories
                            .filter((category) => category.id !== editingCategory?.id)
                            .map((category) => (
                              <option key={category.id} value={category.id}>{category.name}</option>
                            ))}
                        </select>
                        {formErrors.parentCategoryId ? (
                          <p className="mt-1 text-sm text-red-600">{formErrors.parentCategoryId}</p>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : null}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Description de la catégorie..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL de l'image
                  </label>
                  <input
                    type="url"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.imageUrl ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="https://example.com/image.jpg"
                  />
                  {formErrors.imageUrl && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.imageUrl}</p>
                  )}
                  {formData.imageUrl && !formErrors.imageUrl && (
                    <div className="mt-2">
                      <img
                        src={formData.imageUrl}
                        alt="Aperçu"
                        className="w-full h-32 object-cover rounded-lg"
                        onError={() => setFormErrors({ ...formErrors, imageUrl: 'Image non accessible' })}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Sauvegarde...
                    </>
                  ) : (
                    editingCategory ? 'Mettre à jour' : 'Créer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;
